import {useEffect, useState} from "react";
import {Button, Input, InputGroup, Loader, Modal, RadioTile, RadioTileGroup, Table, Tabs, toaster, Uploader} from "rsuite";
import Tab from "rsuite/Tab";
import {ContourType, EspType} from "../../../api/artifacts";
import {ArtifactsApi} from "../../../services/Artifacts.api";
import {
  actionConfigs,
  ESP_TYPE_OPTIONS,
  EspFirmwareFormState,
  EspFirmwareModalProps,
  FirmwareAction,
  FirmwareHistoryItem,
  FirmwareModalTab,
} from "../Firmwares.model";
import NotificationFactory from "../../../shared/Notifications";
import {FirmwaresUtils} from "../Firmwares.util";
import {TfiClipIcon} from "../../../models/Icons.model";

const {Column, HeaderCell, Cell} = Table;

const EspFirmwareModal = ({open, mode, firmware, onClose, onSuccess}: EspFirmwareModalProps) => {
  const [form, setForm] = useState<EspFirmwareFormState>(() => FirmwaresUtils.getEspFirmwareInitialState(firmware));
  const [modalFirmware, setModalFirmware] = useState(firmware);
  const [fileList, setFileList] = useState<any[]>([]);
  const [uploaderKey, setUploaderKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [parseLoading, setParseLoading] = useState(false);
  const [isVersionLocked, setIsVersionLocked] = useState(false);
  const [isEspTypeLocked, setIsEspTypeLocked] = useState(false);
  const [activeTab, setActiveTab] = useState<FirmwareModalTab>("general");
  const [historyItems, setHistoryItems] = useState<FirmwareHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [pendingAction, setPendingAction] = useState<FirmwareAction | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setModalFirmware(null);
      return;
    }

    setModalFirmware(firmware);
    setForm(FirmwaresUtils.getEspFirmwareInitialState(firmware));
    setFileList([]);
    setUploaderKey(0);
    setIsVersionLocked(false);
    setIsEspTypeLocked(false);
    setPendingAction(null);
    setActiveTab("general");
    setHistoryItems([]);
    setHistoryLoaded(false);

    if (mode !== "edit" || !firmware?.id) {
      setDetailsLoading(false);
      return;
    }

    let isActual = true;

    setDetailsLoading(true);

    ArtifactsApi.espFirmwares.getById(firmware.id)
      .then((response) => {
        if (!isActual) return;

        const detailedFirmware = response as EspFirmwareModalProps["firmware"];

        setModalFirmware(detailedFirmware);
        setForm(FirmwaresUtils.getEspFirmwareInitialState(detailedFirmware));
      })
      .catch((error) => {
        if (!isActual) return;

        console.error("Ошибка получения информации об ESP прошивке:", error);
        toaster.push(NotificationFactory.error("Ошибка получения информации о прошивке"), {placement: "topEnd"});
      })
      .finally(() => {
        if (isActual) {
          setDetailsLoading(false);
        }
      });

    return () => {
      isActual = false;
    };
  }, [open, firmware, mode]);

  const currentFirmware = modalFirmware || firmware;

  const loadHistory = async () => {
    if (!currentFirmware?.id || historyLoaded || historyLoading) return;

    setHistoryLoading(true);

    try {
      const response = await ArtifactsApi.espFirmwares.getHistory(currentFirmware.id);

      setHistoryItems(FirmwaresUtils.getItems<FirmwareHistoryItem>(response));
      setHistoryLoaded(true);
    } catch (error) {
      console.error("Ошибка получения истории изменений ESP прошивки:", error);
      toaster.push(NotificationFactory.error("Ошибка получения истории изменений"), {placement: "topEnd"});
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "history") {
      loadHistory().then();
    }
  }, [activeTab]);

  const handleChange = <K extends keyof EspFirmwareFormState>(field: K, value: EspFirmwareFormState[K]) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const selectedFile = fileList[0];
  const selectedFileSize = selectedFile?.blobFile?.size;
  const selectedFileSizeMb = typeof selectedFileSize === "number"
    ? `${(selectedFileSize / (1024 * 1024)).toFixed(2)} MB`
    : null;

  const handleFileChange = async (files: any[]) => {
    setFileList(files);
    const file = files[0]?.blobFile || null;

    handleChange("file", file);

    if (!file) {
      return;
    }

    setParseLoading(true);

    try {
      const response = await ArtifactsApi.espFirmwares.parse(file);
      const parsedVersion = response?.version?.trim() || "";
      const parsedEspType = response?.espType ? FirmwaresUtils.getEspType(response.espType) : null;

      if (parsedVersion) {
        handleChange("version", parsedVersion);
        setIsVersionLocked(true);
      } else {
        setIsVersionLocked(false);
      }

      if (parsedEspType) {
        handleChange("espType", parsedEspType);
        setIsEspTypeLocked(true);
      } else {
        setIsEspTypeLocked(false);
      }
    } catch (error) {
      console.error("Ошибка разбора ESP прошивки:", error);
      setIsVersionLocked(false);
      setIsEspTypeLocked(false);
      toaster.push(NotificationFactory.error("Не удалось определить версию из файла прошивки"), {placement: "topEnd"});
    } finally {
      setParseLoading(false);
    }
  };

  const handleFileRemove = () => {
    setFileList([]);
    setUploaderKey((prev) => prev + 1);
    setIsVersionLocked(false);
    setIsEspTypeLocked(false);
    handleChange("file", null);
    handleChange("version", null);
    handleChange("espType", null);
  };

  const handleTabSelect = (eventKey?: string | number) => {
    setActiveTab(eventKey === "history" ? "history" : "general");
  };

  const handleSubmit = async () => {
    const missingFields = FirmwaresUtils.getMissingRequiredFields([
      {label: "Версия", value: form.version},
      {label: "Описание", value: form.description},
      {label: "Устройство", value: form.espType},
    ]);

    if (missingFields.length) {
      toaster.push(
        NotificationFactory.error(FirmwaresUtils.getRequiredFieldsMessage(missingFields)),
        {placement: "topEnd"}
      );
      return;
    }

    setLoading(true);

    try {
      if (mode === "create") {
        await ArtifactsApi.espFirmwares.create({
          version: form.version || null,
          description: form.description || null,
          espType: form.espType || undefined,
          file: form.file,
        });
      } else if (currentFirmware?.id) {
        await ArtifactsApi.espFirmwares.update(currentFirmware.id, {
          version: form.version || null,
          description: form.description || null,
          espType: form.espType || undefined,
        });

        if (form.file) {
          await ArtifactsApi.espFirmwares.updateFile(currentFirmware.id, form.file);
        }
      }

      toaster.push(NotificationFactory.success(FirmwaresUtils.getSuccessMessage(mode)), {placement: "topEnd"});
      onSuccess();
    } catch (error) {
      console.error("Ошибка сохранения ESP прошивки:", error);
      toaster.push(NotificationFactory.error(FirmwaresUtils.getErrorMessage(error, mode)), {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!currentFirmware?.id) return;

    setDownloadLoading(true);

    try {
      const blob = await ArtifactsApi.espFirmwares.download(currentFirmware.id);
      const fileName = currentFirmware.fileDescriptor?.name || `${currentFirmware.version || "esp-firmware"}.bin`;

      FirmwaresUtils.downloadBlob(blob, fileName);
    } catch (error) {
      console.error("Ошибка скачивания файла ESP прошивки:", error);
      toaster.push(NotificationFactory.error("Ошибка скачивания файла прошивки"), {placement: "topEnd"});
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleConfirmAction = async () => {
    if (!currentFirmware?.id || !pendingAction) return;

    setActionLoading(true);

    try {
      if (pendingAction === "delete") {
        await ArtifactsApi.espFirmwares.delete(currentFirmware.id);
        toaster.push(NotificationFactory.success("Прошивка удалена"), {placement: "topEnd"});
      } else if (pendingAction === "removeFromTest" || pendingAction === "removeFromProduction") {
        const contour: ContourType = pendingAction === "removeFromTest" ? "Test" : "Production";
        const contourLabel = pendingAction === "removeFromTest" ? "Test" : "One";

        await ArtifactsApi.espFirmwares.removeFromContour(currentFirmware.id, {contour});
        toaster.push(NotificationFactory.success(`Прошивка снята с ${contourLabel}`), {placement: "topEnd"});
      } else {
        const contour: ContourType = pendingAction === "deployTest" ? "Test" : "Production";
        const contourLabel = pendingAction === "deployTest" ? "Test" : "One";

        await ArtifactsApi.espFirmwares.deploy(currentFirmware.id, {contour});
        toaster.push(NotificationFactory.success(`Прошивка отправлена на ${contourLabel}`), {placement: "topEnd"});
      }

      setPendingAction(null);
      onSuccess();
    } catch (error) {
      console.error("Ошибка выполнения действия с ESP прошивкой:", error);
      toaster.push(NotificationFactory.error(FirmwaresUtils.getErrorMessage(error, "edit")), {placement: "topEnd"});
    } finally {
      setActionLoading(false);
    }
  };

  const actionConfig = pendingAction ? actionConfigs[pendingAction] : null;

  const renderHistory = () => {
    if (mode === "create") {
      return <div className="firmware-modal__history-empty">История появится после создания прошивки</div>;
    }

    if (historyLoading) {
      return (
        <div className="firmware-modal__history-loader">
          <Loader content="Загрузка истории..." />
        </div>
      );
    }

    if (!historyItems.length) {
      return <div className="firmware-modal__history-empty">История изменений пуста</div>;
    }

    return (
      <Table autoHeight data={historyItems} rowHeight={60} className="firmware-modal__history-table" wordWrap>
        <Column width={120} verticalAlign="middle" fullText>
          <HeaderCell>Действие</HeaderCell>
          <Cell dataKey="action">
            {(rowData: FirmwareHistoryItem) => rowData.action || "-"}
          </Cell>
        </Column>

        <Column flexGrow={1} minWidth={220} verticalAlign="middle" fullText>
          <HeaderCell>Комментарий</HeaderCell>
          <Cell dataKey="comment">
            {(rowData: FirmwareHistoryItem) => rowData.comment || "-"}
          </Cell>
        </Column>

        <Column width={240} verticalAlign="middle" fullText>
          <HeaderCell>Автор</HeaderCell>
          <Cell dataKey="performedBy">
            {(rowData: FirmwareHistoryItem) => rowData.performedBy || "-"}
          </Cell>
        </Column>

        <Column width={150} verticalAlign="middle" fullText>
          <HeaderCell>Дата</HeaderCell>
          <Cell dataKey="createdAt">
            {(rowData: FirmwareHistoryItem) => FirmwaresUtils.formatDate(rowData.createdAt)}
          </Cell>
        </Column>
      </Table>
    );
  };

  return (
    <>
      <Modal open={open} onClose={onClose} size="md" backdrop="static">
        <Modal.Header>
          <Modal.Title>{FirmwaresUtils.getModalTitle(mode)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs activeKey={activeTab} onSelect={handleTabSelect}>
            <Tab eventKey="general" title="Общее">
              <div className="mb-3 firmware-modal__selected-file-row">
                {mode !== "edit" && (
                  <Uploader
                    key={`esp-firmware-upload-${uploaderKey}`}
                    accept=".bin"
                    fileList={fileList}
                    fileListVisible={false}
                    autoUpload={false}
                    multiple={false}
                    listType="picture-text"
                    onChange={(files) => {
                      void handleFileChange(files);
                    }}
                    action=""
                  >
                    <Button color="blue" appearance="primary">
                      Добавить файл
                    </Button>
                  </Uploader>
                )}

                {selectedFile && (
                  <div className="firmware-modal__selected-file">
                    <div className="firmware-modal__selected-file-icon">
                      <TfiClipIcon />
                    </div>
                    <div className="firmware-modal__selected-file-info">
                      <div className="firmware-modal__selected-file-name">
                        {selectedFile.name || "Файл выбран"}
                        {selectedFileSizeMb && (
                        <span className="firmware-modal__selected-file-size ms-2">{selectedFileSizeMb}</span>
                      )}
                      </div>

                    </div>
                    <button
                      type="button"
                      className="firmware-modal__selected-file-remove"
                      onClick={handleFileRemove}
                      aria-label="Удалить выбранный файл"
                    >
                      ×
                    </button>
                  </div>
                )}

                {parseLoading && <Loader content="Определение версии..." />}
              </div>

              <InputGroup className="mb-3">
                <InputGroup.Addon>Версия</InputGroup.Addon>
                <Input
                  disabled={isVersionLocked}
                  value={form.version ?? ""}
                  onChange={(value) => handleChange("version", value)}
                />
              </InputGroup>

              <InputGroup className="mb-3">
                <Input
                  as="textarea"
                  rows={4}
                  value={form.description}
                  placeholder="Описание"
                  onChange={(value) => handleChange("description", value)}
                />
              </InputGroup>

              <div className="mb-3">
                <RadioTileGroup
                  as="div"
                  aria-label="ESP type"
                  className={`w-100 firmware-modal__esp-type-group${isEspTypeLocked ? " firmware-modal__esp-type-group--readonly" : ""}`}
                  disabled={isEspTypeLocked}
                  value={form.espType || undefined}
                  onChange={isEspTypeLocked ? undefined : (value) => handleChange("espType", value as EspType)}
                >
                  {ESP_TYPE_OPTIONS.map((option) => (
                    <RadioTile
                      key={option.value}
                      className="mb-0"
                      value={option.value}
                      label={option.label}
                    >
                      <span className="radio-tile-text">{option.label}</span>
                    </RadioTile>
                  ))}
                </RadioTileGroup>
              </div>

              <div className="d-flex align-items-start flex-wrap gap-2">
                <div className="firmware-modal__file-actions">
                  {mode === "edit" && (
                    <Uploader
                      key={`esp-firmware-reupload-${uploaderKey}`}
                      accept=".bin"
                      fileList={[]}
                      fileListVisible={false}
                      autoUpload={false}
                      multiple={false}
                      listType="picture-text"
                      onChange={(files) => {
                        void handleFileChange(files);
                      }}
                      action=""
                    >
                      <Button color="blue" appearance="primary">
                        Обновить файл
                      </Button>
                    </Uploader>
                  )}

                  {mode === "edit" && (
                    <Button
                      appearance="primary"
                      color="blue"
                      className="firmware-modal__download-button"
                      loading={downloadLoading}
                      disabled={loading || detailsLoading || actionLoading || downloadLoading || !currentFirmware?.id || !currentFirmware?.fileDescriptor?.name}
                      onClick={handleDownload}
                    >
                      Скачать
                    </Button>
                  )}
                </div>

                {mode === "edit" && (
                  <div className="firmware-modal__contour-actions">
                    <div className="firmware-modal__action-row">
                      {currentFirmware?.isDeployedToTest ? (
                        <Button
                          appearance="primary"
                          color="blue"
                          className="firmware-modal__action-button"
                          disabled={loading || detailsLoading || actionLoading || !currentFirmware?.id}
                          onClick={() => setPendingAction("removeFromTest")}
                        >
                          Снять с Test
                        </Button>
                      ) : (
                        <Button
                          appearance="primary"
                          color="green"
                          className="firmware-modal__action-button"
                          disabled={loading || detailsLoading || actionLoading || !currentFirmware?.id}
                          onClick={() => setPendingAction("deployTest")}
                        >
                          Деплой Test
                        </Button>
                      )}
                      {currentFirmware?.isDeployedToProduction ? (
                        <Button
                          appearance="primary"
                          color="blue"
                          className="firmware-modal__action-button"
                          disabled={loading || detailsLoading || actionLoading || !currentFirmware?.id}
                          onClick={() => setPendingAction("removeFromProduction")}
                        >
                          Снять с One
                        </Button>
                      ) : (
                        <Button
                          appearance="primary"
                          color="green"
                          className="firmware-modal__action-button"
                          disabled={loading || detailsLoading || actionLoading || !currentFirmware?.id}
                          onClick={() => setPendingAction("deployProduction")}
                        >
                          Деплой One
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {mode === "edit" && (
                  <Button
                    appearance="primary"
                    color="red"
                    className="firmware-modal__action-button"
                    disabled={loading || detailsLoading || actionLoading || !currentFirmware?.id}
                    onClick={() => setPendingAction("delete")}
                  >
                    Удалить
                  </Button>
                )}
              </div>
            </Tab>

            <Tab eventKey="history" title="История изменений">
              {renderHistory()}
            </Tab>
          </Tabs>
        </Modal.Body>
        {activeTab === "general" && (
          <Modal.Footer>
            <Button appearance="primary" onClick={handleSubmit} loading={loading} disabled={loading || detailsLoading || actionLoading || parseLoading}>
              Сохранить
            </Button>
            <Button appearance="subtle" onClick={onClose} disabled={loading || actionLoading}>
              Отмена
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      <Modal open={Boolean(pendingAction)} onClose={() => setPendingAction(null)} backdrop="static" size="xs">
        <Modal.Header>
          <Modal.Title>{actionConfig?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{actionConfig?.message}</Modal.Body>
        <Modal.Footer>
          <Button appearance="subtle" onClick={() => setPendingAction(null)} disabled={actionLoading}>
            Отмена
          </Button>
          <Button
            appearance="primary"
            color={actionConfig?.color}
            onClick={handleConfirmAction}
            loading={actionLoading}
            disabled={actionLoading}
          >
            {actionConfig?.confirmText}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EspFirmwareModal;
